import { cn } from "@/utils";
import { useEffect, useRef } from "react";

export type VoiceWaveformState =
  | "live"
  | "connecting"
  | "reconnecting"
  | "paused"
  | "idle";

interface VoiceWaveformProps {
  /**
   * Polled from an internal rAF loop — audio levels never flow through React
   * state. `history` is the rolling amplitude history (oldest → newest).
   */
  getLevels: () => { level: number; history: readonly number[] };
  state: VoiceWaveformState;
  className?: string;
}

const BAR_WIDTH = 3;
const BAR_GAP = 3;
const MIN_BAR = 2;

/**
 * Voice-Memos-style scrolling amplitude waveform for the mobile voice screen.
 *
 * Canvas-based and devicePixelRatio-aware; bars scroll right→left as history
 * accumulates, with the newest bar accented rust while live. Reconnecting
 * desaturates and pulses; paused freezes at reduced opacity. Colors are read
 * from the computed style so the component follows the theme: the canvas's
 * own `color` paints the bars, so pass text classes to restyle.
 */
export function VoiceWaveform({
  getLevels,
  state,
  className,
}: VoiceWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const accentRef = useRef<HTMLSpanElement | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const getLevelsRef = useRef(getLevels);
  getLevelsRef.current = getLevels;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    let frame: number | null = null;
    let colors = { bar: "#888", accent: "#B86E49" };
    const readColors = () => {
      colors = {
        bar: getComputedStyle(canvas).color || colors.bar,
        accent: accentRef.current
          ? getComputedStyle(accentRef.current).color || colors.accent
          : colors.accent,
      };
    };
    readColors();

    const draw = () => {
      frame = requestAnimationFrame(draw);
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width === 0 || height === 0) return;
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
      }
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);

      const currentState = stateRef.current;
      const { level, history } = getLevelsRef.current();
      const slots = Math.floor(width / (BAR_WIDTH + BAR_GAP));
      const visible = history.slice(-slots);

      let alpha = 1;
      if (currentState === "paused") alpha = 0.35;
      if (currentState === "reconnecting" || currentState === "connecting") {
        // Gentle 1.2s pulse while the connection is away.
        alpha = 0.35 + 0.25 * (0.5 + 0.5 * Math.sin(performance.now() / 190));
      }
      context.globalAlpha = alpha;

      const mid = height / 2;
      // Older bars from the right edge leftward, newest at the right.
      for (let i = 0; i < visible.length; i++) {
        const isNewest = i === visible.length - 1;
        const value = isNewest && currentState === "live" ? level : visible[i];
        const barHeight = Math.max(MIN_BAR, value * (height - 4));
        const x =
          width - (visible.length - i) * (BAR_WIDTH + BAR_GAP) + BAR_GAP;
        context.fillStyle =
          isNewest && currentState === "live" ? colors.accent : colors.bar;
        drawRoundedBar(context, x, mid - barHeight / 2, BAR_WIDTH, barHeight);
      }

      // Idle/empty: a resting baseline of dots so the surface reads as ready.
      if (visible.length === 0) {
        context.fillStyle = colors.bar;
        context.globalAlpha = 0.3;
        for (let x = width - BAR_GAP; x > 0; x -= BAR_WIDTH + BAR_GAP) {
          drawRoundedBar(context, x, mid - MIN_BAR / 2, BAR_WIDTH, MIN_BAR);
        }
      }
      context.globalAlpha = 1;
    };
    frame = requestAnimationFrame(draw);

    // Colors depend on the theme; re-read when the document class flips.
    const observer = new MutationObserver(readColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-mode"],
    });

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={cn("relative w-full", className)}>
      {/* Hidden accent swatch: resolves the rust token to a computed color. */}
      <span
        ref={accentRef}
        className="text-rust absolute h-0 w-0 overflow-hidden"
      />
      <canvas
        ref={canvasRef}
        className="h-full w-full text-foreground/60"
        aria-hidden
      />
    </div>
  );
}

function drawRoundedBar(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const radius = width / 2;
  context.beginPath();
  context.moveTo(x, y + radius);
  context.arcTo(x, y, x + width, y, radius);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.closePath();
  context.fill();
}

export default VoiceWaveform;
